<template>
  <div>
    <validation-observer ref="simpleRules">
      <b-card class="account-member-form-card">
        <b-form @submit.prevent>
          <b-row>
            <b-col
              cols="12"
              class="mb-2"
            >
              <b-card class="border h-100 mb-0">
                <div class="d-flex align-items-center mb-2">
                  <feather-icon
                    icon="UserIcon"
                    size="18"
                    class="mr-50 text-primary"
                  />
                  <h5 class="mb-0">
                    {{ $t('accountMembers.sections.generalInformation') }}
                  </h5>
                </div>

                <b-row>
                  <b-col
                    cols="12"
                    md="6"
                  >
                    <b-form-group
                      :label="$t('accountMembers.fields.username')"
                      label-for="mc-username"
                    >
                      <validation-provider
                        #default="{ errors }"
                        :name="$t('accountMembers.fields.username')"
                        rules="required"
                      >
                        <b-form-input
                          id="mc-username"
                          v-model="form.username"
                          :placeholder="$t('accountMembers.placeholders.username')"
                          disabled
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>

                  <b-col
                    cols="12"
                    md="6"
                  >
                    <b-form-group
                      :label="$t('accountMembers.fields.nickname')"
                      label-for="mc-nickname"
                    >
                      <validation-provider
                        #default="{ errors }"
                        :name="$t('accountMembers.fields.nickname')"
                        rules="required"
                      >
                        <b-form-input
                          id="mc-nickname"
                          v-model="form.nickName"
                          :placeholder="$t('accountMembers.placeholders.nickname')"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>

                  <b-col
                    cols="12"
                    md="6"
                  >
                    <b-form-group
                      :label="$t('accountMembers.fields.password')"
                      label-for="change-password"
                    >
                      <b-button
                        v-ripple.400="'var(--hdg-ripple-primary)'"
                        type="button"
                        variant="outline-primary"
                        block
                        @click.prevent="modalPassword = true"
                      >
                        <feather-icon
                          icon="LockIcon"
                          size="14"
                          class="mr-50"
                        />
                        {{ $t('Change Password') }}
                      </b-button>
                    </b-form-group>
                  </b-col>

                  <b-col
                    cols="12"
                    md="6"
                  >
                    <b-form-group
                      :label="$t('accountMembers.fields.phone')"
                      label-for="mc-phone"
                      class="mb-0"
                    >
                      <validation-provider
                        #default="{ errors }"
                        :name="$t('accountMembers.fields.phone')"
                        rules="required|digits:10"
                      >
                        <b-form-input
                          id="mc-phone"
                          v-model="form.phone"
                          :placeholder="$t('accountMembers.placeholders.phone')"
                          minlength="10"
                          maxlength="10"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-card>
            </b-col>

            <b-col
              cols="12"
              class="mb-2"
            >
              <b-card class="border h-100 mb-0">
                <div class="d-flex align-items-center mb-2">
                  <feather-icon
                    icon="ShieldIcon"
                    size="18"
                    class="mr-50 text-primary"
                  />
                  <h5 class="mb-0">
                    {{ $t('accountMembers.sections.permission') }}
                  </h5>
                </div>

                <b-form-group
                  :label="$t('Permission')"
                  label-for="permission"
                  class="mb-0"
                >
                  <b-button
                    id="permission"
                    v-ripple.400="'var(--hdg-ripple-primary)'"
                    type="button"
                    variant="outline-primary"
                    block
                    @click.prevent="getAbility"
                  >
                    <feather-icon
                      icon="ShieldIcon"
                      size="14"
                      class="mr-50"
                    />
                    {{ $t('Edit Permission') }}
                  </b-button>
                </b-form-group>
              </b-card>
            </b-col>

            <b-col
              v-if="canViewLoginSecurity()"
              cols="12"
              class="mb-2"
            >
              <b-card class="border h-100 mb-0">
                <div class="d-flex align-items-center mb-2">
                  <feather-icon
                    icon="ShieldIcon"
                    size="18"
                    class="mr-50 text-primary"
                  />
                  <h5 class="mb-0">
                    {{ $t('Two-factor authentication') }}
                  </h5>
                </div>

                <b-row>
                  <b-col
                    cols="12"
                    :md="canShowTotpResetAction() ? 6 : 12"
                  >
                    <b-form-group
                      :label="$t('Status')"
                      label-for="totp-status"
                      class="mb-md-0"
                    >
                      <div
                        id="totp-status"
                        class="form-control totp-status-control d-flex align-items-center"
                      >
                        <b-badge
                          :variant="getTotpStatusVariant()"
                          class="text-nowrap"
                        >
                          {{ getTotpStatusText() }}
                        </b-badge>
                      </div>
                    </b-form-group>
                  </b-col>

                  <b-col
                    v-if="canShowTotpResetAction()"
                    cols="12"
                    md="6"
                    class="d-flex align-items-end"
                  >
                    <b-button
                      v-ripple.400="'var(--hdg-ripple-primary)'"
                      type="button"
                      variant="outline-primary"
                      block
                      :disabled="!canResetTotp() || totpResetting"
                      @click.prevent="resetTotp"
                    >
                      <feather-icon
                        icon="RefreshCwIcon"
                        size="14"
                        class="mr-50"
                      />
                      {{ $t('Reset 2FA') }}
                    </b-button>
                  </b-col>
                </b-row>
              </b-card>
            </b-col>

            <b-col
              cols="12"
              class="mb-2"
            >
              <b-card class="border h-100 mb-0">
                <div class="d-flex align-items-center mb-2">
                  <feather-icon
                    icon="ToggleRightIcon"
                    size="18"
                    class="mr-50 text-primary"
                  />
                  <h5 class="mb-0">
                    {{ $t('Status') }}
                  </h5>
                </div>

                <b-row>
                  <b-col
                    cols="12"
                    md="6"
                  >
                    <b-form-group
                      :label="$t('Suspend')"
                      label-for="suspend-status"
                      class="mb-md-0"
                    >
                      <b-form-input
                        id="suspend-status"
                        disabled
                        :value="isEnable === true ? $t('No') : $t('Yes')"
                      />
                    </b-form-group>
                  </b-col>

                  <b-col
                    cols="12"
                    md="6"
                    class="d-flex align-items-end"
                  >
                    <b-button
                      v-ripple.400="'var(--hdg-ripple-primary)'"
                      type="button"
                      variant="outline-primary"
                      block
                      @click.prevent="submitStatus"
                    >
                      <feather-icon
                        icon="RefreshCwIcon"
                        size="14"
                        class="mr-50"
                      />
                      {{ $t('Edit Status') }}
                    </b-button>
                  </b-col>
                </b-row>
              </b-card>
            </b-col>
          </b-row>

          <b-row>
            <b-col
              cols="12"
              class="text-right"
            >
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                type="submit"
                variant="primary"
                size="lg"
                class="d-inline-flex align-items-center"
                @click.prevent="onSubmitForm"
              >
                <feather-icon
                  icon="SaveIcon"
                  size="18"
                  class="mr-50"
                />
                {{ $t('accountMembers.actions.submit') }}
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </b-card>
      <b-modal
        v-model="modalPassword"
        cancel-variant="secondary"
        ok-only
        :ok-title="$t('Submit')"
        centered
        :title="$t('เปลี่ยนรหัสผ่าน')"
        button-size="sm"
        @ok="submitPassword"
      >
        <b-form @submit.prevent>
          <b-form-group
            :label="$t('Password')"
            label-for="mc-password"
          >
            <validation-provider
              #default="{ errors }"
              name="Password"
              vid="Password"
              rules="required|min:8|password"
            >
              <b-form-input
                id="mc-password"
                v-model="passwordForm.password"
                autofocus
                :placeholder="$t('Password')"
                :state="errors.length > 0 ? false:null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
          <b-form-group
            :label="$t('Confirm Password')"
            label-for="mc-confirm-password"
          >
            <validation-provider
              #default="{ errors }"
              name="Confirm Password"
              rules="required|confirmed:Password"
            >
              <b-form-input
                id="mc-confirm-password"
                v-model="passwordForm.passwordConfirm"
                :state="errors.length > 0 ? false:null"
                :placeholder="$t('Confirm Password')"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
        </b-form>
      </b-modal>
      <b-modal
        v-model="abilityDisble"
        cancel-variant="secondary"
        ok-only
        :ok-title="$t('Submit')"
        centered
        :title="$t('สิทธิ์การเข้าใช้งาน')"
        button-size="sm"
        size="sm"
        @ok="submitAbility"
      >
        <AbilityForm
          ref="abilityForm"
          :ability="ability"
          @onSubmit="onSubmit"
        />
      </b-modal>
      <TotpResetDialog ref="totpResetDialog" />
    </validation-observer>
  </div>
</template>

<script>
/* eslint-disable */
import {
  BBadge, BCard, BRow, BCol, BFormGroup, BFormInput, BForm, BButton, BModal,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import {} from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import AbilityForm from '@/views/components/AbilityForm'
import agentService from '@/service/agentService'
import useJwt from '@/auth/jwt/useJwt'
import TotpResetDialog from './TotpResetDialog.vue'

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    BBadge,
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BButton,
    BModal,
    AbilityForm,
    TotpResetDialog,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      abilityDisble: false,
      ability: {},
      form: {
        username: '',
        phone: '',
        nickName: '',
      },
      totpStatus: {
        loading: true,
        required: false,
        enabled: false,
        enrolledAt: null,
        unknown: false,
      },
      totpResetting: false,
      isEnable: false,

      modalPassword: false,
      passwordForm: {
        password: '',
        passwordConfirm: '',
      }
    }
  },
  async mounted() {
    const username = this.$router.currentRoute.query.id
    if (username !== null && username !== undefined) {
      const res = await agentService.getAuthen(`subAccount/find?username=${username}`)
      if (res.status === 200) {
        this.form.username = res.data.username
        this.form.phone = res.data.phone
        this.form.nickName = res.data.nickName

        // this.passwordForm.password = res.data.password
        // this.passwordForm.passwordConfirm = res.data.password

        this.isEnable = res.data.isEnable
        await this.loadTotpStatus()
      }
    }
  },
  methods: {
    canViewLoginSecurity() {
      return this.$can('read', 'loginSecurity') || this.$can('update', 'loginSecurity') || this.$can('manage', 'all')
    },
    canUpdateLoginSecurity() {
      return this.$can('update', 'loginSecurity') || this.$can('manage', 'all')
    },
    canShowTotpResetAction() {
      return this.canUpdateLoginSecurity()
    },
    canResetTotp() {
      return this.totpStatus.required
        && this.totpStatus.enabled
        && !this.totpStatus.loading
        && !this.totpStatus.unknown
        && this.canUpdateLoginSecurity()
    },
    async loadTotpStatus() {
      if (!this.canViewLoginSecurity()) return
      if (!this.form.username) {
        this.totpStatus = {
          loading: false,
          required: false,
          enabled: false,
          enrolledAt: null,
          unknown: true,
        }
        return
      }

      this.totpStatus = {
        ...this.totpStatus,
        loading: true,
        unknown: false,
      }

      try {
        const response = await useJwt.loginSecurityTotpStatus({
          usernames: [this.form.username],
        })
        const rows = response.data || []
        const status = rows.find(v => v.username === this.form.username)
        this.totpStatus = {
          loading: false,
          required: status ? status.required : false,
          enabled: status ? status.enabled : false,
          enrolledAt: status ? status.enrolledAt : null,
          unknown: false,
        }
      } catch (error) {
        this.totpStatus = {
          loading: false,
          required: false,
          enabled: false,
          enrolledAt: null,
          unknown: true,
        }
      }
    },
    getTotpStatusText() {
      if (this.totpStatus.loading) return this.$t('Loading status')
      if (this.totpStatus.unknown) return this.$t('Unable to load status')
      if (!this.totpStatus.required) return this.$t('Not required')
      return this.totpStatus.enabled ? this.$t('Enabled') : this.$t('Not enrolled')
    },
    getTotpStatusVariant() {
      if (this.totpStatus.loading) return 'light-secondary'
      if (this.totpStatus.unknown) return 'danger'
      if (!this.totpStatus.required) return 'light-secondary'
      return this.totpStatus.enabled ? 'success' : 'warning'
    },
    async resetTotp() {
      if (!this.canResetTotp()) return

      this.totpResetting = true
      try {
        const resetCompleted = await this.$refs.totpResetDialog.open({
          username: this.form.username,
          submit: code => useJwt.loginSecurityTotpReset({
            username: this.form.username,
            code,
          }),
          getErrorMessage: this.getTotpResetErrorMessage,
        })

        if (!resetCompleted) return

        await this.loadTotpStatus()
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('สำเร็จ'),
            icon: 'CheckIcon',
            variant: 'success',
            text: this.$t('Two-factor authentication reset successfully'),
          },
        })
      } finally {
        this.totpResetting = false
      }
    },
    getTotpResetErrorMessage(error) {
      const data = error.response && error.response.data
      return data && data.error && (data.error.code || data.error.username)
        ? this.translateMessage(data.error.code || data.error.username)
        : this.$t('Unable to reset two-factor authentication. Please try again')
    },
    translateMessage(text) {
      if (!text) return this.$t('Something wrong')
      const message = `${text}`
      const lockedMatch = message.match(/^.+ถูกล็อกชั่วคราว กรุณาลองใหม่ใน (\d+) วินาที$/)
      if (lockedMatch) {
        return this.$t('The authentication app is temporarily locked. Please try again in {seconds} seconds', {
          seconds: lockedMatch[1],
        })
      }
      return this.$t(message)
    },
    submitAbility(e) {
      e.preventDefault()
      this.$refs.abilityForm.onSubmit()
    },
    async getAbility() {
      const res = await agentService.getAuthen(`subAccount/ability?username=${this.form.username}`)
      if (res.status === 200) {
        this.abilityDisble = true
        this.ability = res.data
      }
    },
    onSubmit(v) {
      if (this.form.username === '') {
        return
      }
      v = this.$refs.abilityForm.buildPermission(v);
      this.$swal({
        dark: true,
        title: this.$t('Are you sure?'),
        text: this.$t('คุณเเน่ใจว่าต้องการบันทึกข้อมูลใช่หรือไม่'),
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: this.$t('Ok'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      })
          .then(async result => {
            if (result.value) {
              const res = await agentService.putAuthen(`subAccount/ability?username=${this.form.username}`, v)
              if (res.status === 200) {
                this.abilityDisble = false
                this.$toast({
                  component: ToastificationContent,
                  position: 'top-right',
                  props: {
                    title: this.$t('สำเร็จ'),
                    icon: 'CoffeeIcon',
                    variant: 'success',
                    text: this.$t('บันทึกข้อมูสำเร็จ'),
                  },
                })
              } else {
                this.$toast({
                  component: ToastificationContent,
                  position: 'top-right',
                  props: {
                    title: this.$t('เกิดข้อผิดพลาด'),
                    icon: 'AlertCircleIcon',
                    variant: 'danger',
                    text: this.$t('ขัดคล่อง โปรดลองใหม่ในภายหลัง'),
                  },
                })
              }
            }
          })
    },
    onSubmitForm() {
      this.$refs.simpleRules.validate()
          .then(async success => {
            if (success) {
              this.$swal({
                dark: true,
                title: this.$t('Are you sure?'),
                text: this.$t('คุณเเน่ใจว่าต้องการบันทึกข้อมูลใช่หรือไม่'),
                icon: 'info',
                showCancelButton: true,
                confirmButtonText: this.$t('Ok'),
                customClass: {
                  confirmButton: 'btn btn-primary',
                  cancelButton: 'btn btn-outline-danger ml-1',
                },
                buttonsStyling: false,
              })
                  .then(async result => {
                    if (result.value) {
                      const formSender = { ...this.form }
                      const res = await agentService.putAuthen(`subAccount?username=${this.form.username}`, formSender)
                      if (res.status === 200) {
                        this.$toast({
                          component: ToastificationContent,
                          position: 'top-right',
                          props: {
                            title: this.$t('สำเร็จ'),
                            icon: 'CoffeeIcon',
                            variant: 'success',
                            text: res.data,
                          },
                        })
                      } else {
                        this.$toast({
                          component: ToastificationContent,
                          position: 'top-right',
                          props: {
                            title: this.$t('เกิดข้อผิดพลาด'),
                            icon: 'AlertCircleIcon',
                            variant: 'danger',
                            text: res.data,
                          },
                        })
                      }
                    }
                  })
            }
          })
    },
    submitStatus() {
      this.$refs.simpleRules.validate()
          .then(async success => {
            if (success) {
              this.$swal({
                dark: true,
                title: this.$t('Lock / Unlock Account'),
                text: this.$t('คุณเเน่ใจว่าต้องการดำเนินการใช่หรือไม่ ?'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: this.$t('Yes'),
                customClass: {
                  confirmButton: 'btn btn-primary',
                  cancelButton: 'btn btn-outline-danger ml-1',
                },
                buttonsStyling: false,
              })
                  .then(async result => {
                    if (result.value) {
                      const res = await agentService.putAuthen(`subAccount/toggle-status?username=${this.form.username}`)
                      if (res.status === 200) {
                        this.isEnable = res.data
                        this.$toast({
                          component: ToastificationContent,
                          position: 'top-right',
                          props: {
                            title: this.$t('สำเร็จ'),
                            icon: 'CoffeeIcon',
                            variant: 'success',
                            text: this.$t('ดำเนินการสำเร็จ'),
                          },
                        })
                      } else {
                        this.$toast({
                          component: ToastificationContent,
                          position: 'top-right',
                          props: {
                            title: this.$t('เกิดข้อผิดพลาด'),
                            icon: 'AlertCircleIcon',
                            variant: 'danger',
                            text: res.data,
                          },
                        })
                      }
                    }
                  })
            }
          })
    },
    submitPassword(e) {
      e.preventDefault()
      this.$refs.simpleRules.validate()
          .then(async success => {
            if (success) {
              this.$swal({
                dark: true,
                title: this.$t('Are you sure?'),
                text: this.$t('คุณเเน่ใจว่าต้องการเปลี่ยนรหัสผ่านใช่หรือไม่'),
                icon: 'info',
                showCancelButton: true,
                confirmButtonText: this.$t('Ok'),
                customClass: {
                  confirmButton: 'btn btn-primary',
                  cancelButton: 'btn btn-outline-danger ml-1',
                },
                buttonsStyling: false,
              })
                  .then(async result => {
                    if (result.value) {
                      const formSender = { ...this.passwordForm }
                      delete formSender.passwordConfirm

                      const res = await agentService.putAuthen(`subAccount/password?username=${this.form.username}`, formSender)
                      if (res.status === 200) {
                        this.modalPassword = false;
                        this.$toast({
                          component: ToastificationContent,
                          position: 'top-right',
                          props: {
                            title: this.$t('สำเร็จ'),
                            icon: 'CoffeeIcon',
                            variant: 'success',
                            text: res.data,
                          },
                        })
                      } else {
                        this.$toast({
                          component: ToastificationContent,
                          position: 'top-right',
                          props: {
                            title: this.$t('เกิดข้อผิดพลาด'),
                            icon: 'AlertCircleIcon',
                            variant: 'danger',
                            text: res.data,
                          },
                        })
                      }
                    }
                  })
            }
          })
    }

  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-sweetalert.scss';

.account-member-form-card {
  width: 50%;
}

.form-control.totp-status-control {
  background-color: var(--hdg-panel);
  border-color: var(--hdg-border);
  color: var(--hdg-body);
}

@media (max-width: 991.98px) {
  .account-member-form-card {
    width: 100%;
  }
}
</style>
